/**
 * <sf-collection-meta>
 * Accordion behaviour for the Collection SEO Block.
 *
 * Progressive enhancement: the section renders with panels open. On connect we
 * add `.is-enhanced` (CSS then collapses panels) and wire up the toggles, so a
 * no-JS visitor — and crawlers — still get the full, readable content.
 *
 * Each toggle button carries aria-controls pointing at its panel id. Clicking
 * flips aria-expanded and the panel's `.is-open` class (depth-independent, so
 * top-level accordions and nested FAQ items share one handler).
 */
class SfCollectionMeta extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-enhanced');

    this.toggles = Array.from(this.querySelectorAll('.js-scm-toggle'));
    this.toggles.forEach((btn) => {
      const panel = this._panelFor(btn);
      if (panel) {
        // Sync initial state from the server-rendered aria-expanded.
        panel.classList.toggle('is-open', btn.getAttribute('aria-expanded') === 'true');
      }
      btn.addEventListener('click', this._onToggle.bind(this));
    });
  }

  disconnectedCallback() {
    if (!this.toggles) return;
    this.toggles.forEach((btn) => btn.removeEventListener('click', this._onToggle.bind(this)));
  }

  _panelFor(btn) {
    const id = btn.getAttribute('aria-controls');
    return id ? this.querySelector('#' + CSS.escape(id)) : null;
  }

  _onToggle(event) {
    const btn = event.currentTarget;
    const panel = this._panelFor(btn);
    if (!panel) return;
    const open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
    panel.classList.toggle('is-open', !open);
  }
}

if (!customElements.get('sf-collection-meta')) {
  customElements.define('sf-collection-meta', SfCollectionMeta);
}
